import React from 'react';
import styled from 'styled-components';
import {Spin} from 'antd';
import Calendar from './Calendar';
import moment from 'moment';
import * as AppContext from '../AppContext';
import * as Icon from './Icon';
import {LOGISTICE_TYPE_CATEGORY} from '../domain/Constants';
import {generateDates} from '../utils/date';
import Gtag from '../utils/gtag';

const _HARD_CODE_DISABLE_DAYS = {
  [LOGISTICE_TYPE_CATEGORY.selfPick]: [
    '2022-01-31',
    '2022-02-01',
    '2022-02-02',
    '2022-02-03',
    '2022-02-04',
    '2023-01-21',
    '2023-01-22',
    '2023-01-23',
    '2023-01-24',
    '2023-01-25',
  ],
  [LOGISTICE_TYPE_CATEGORY.home]: [
    '2022-01-28',
    '2022-01-29',
    '2022-01-30',
    '2022-01-31',
    '2022-02-01',
    '2022-02-02',
    '2022-02-03',
    '2022-02-04',
    '2022-02-05',
    '2022-02-06',
    '2022-02-07',
    '2023-01-18',
    '2023-01-19',
    '2023-01-20',
    '2023-01-21',
    '2023-01-22',
    '2023-01-23',
    '2023-01-24',
    '2023-01-25',
    '2023-01-26',
    '2023-01-27',
    '2023-01-28',
    '2023-01-29',
    '2023-01-30',
  ],
};

function ReserveCalendar(props) {
  const {
    item,
    defaultSelectedDateStr = null,
    onSelect = () => 0,
    isStaff = false,
    product = null,
  } = props;
  const [availableDates, setAvailableDates] = React.useState([]);
  const app = React.useContext(AppContext.Context);
  const [isLoading, setIsLoading] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const tomorrow = new Date(Date.now() + 24 * 3600 * 1000);
  const _minDate = React.useMemo(() => new Date(), []);
  const _maxDate = React.useMemo(
    () =>
      isStaff
        ? moment().add(3, 'years').toDate()
        : moment().add(3, 'months').toDate(),
    [],
  );

  const _selectedDate = React.useMemo(() => {
    let _result = defaultSelectedDateStr
      ? moment(defaultSelectedDateStr, 'YYYY-MM-DD').toDate()
      : null;
    return _result;
  }, [defaultSelectedDateStr]);

  const _maxDateCouldBuy = React.useMemo(() => {
    let _maxAvailableDate = null;
    if (availableDates.length > 0) {
      let str = availableDates
        .slice()
        .sort((a, b) => (a.date > b.date ? -1 : 1))[0].date;
      _maxAvailableDate = moment(str, 'YYYY-MM-DD');
    }
    return _maxAvailableDate || _maxDate;
  }, [availableDates, _maxDate]);

  // NOTE: item is directly connect to stock, which means item is "product + spec + 取貨方式" (草莓蛋糕＋七吋＋宅配)
  React.useEffect(() => {
    const _fetchCalendar = async () => {
      setIsLoading(true);
      try {
        let _data = await app.actions.fetchCalendar({
          from: moment(_minDate).format('YYYY-MM-DD'),
          to: moment(_maxDate).format('YYYY-MM-DD'),
          itemId: item.id,
          logisticType: item.logisticType,
        });

        // get entp_stock
        let entpStockMap = {};
        let duplicateEntpIds = _data
          .filter((d) => !!d.entp_stock_id)
          .map((d) => d.entp_stock_id);
        let entpIds = Array.from(new Set(duplicateEntpIds));
        if (entpIds.length > 0) {
          let entpStocks = await app.actions.fetchEntpStocks(entpIds);
          entpStockMap = entpStocks.reduce((acc, curr, idx) => {
            acc[curr.id] = curr;
            return acc;
          }, {});
        }

        let availableDates = _data
          .filter(
            // isStaff want to show all stock data
            (d) =>
              isStaff
                ? true
                : d.book_dates.indexOf(moment().format('YYYY-MM-DD')) !== -1,
          )
          .filter(
            (d) =>
              _HARD_CODE_DISABLE_DAYS[item.logisticType].indexOf(
                d.delivery_date,
              ) === -1,
          )
          .map((d) => ({
            stock: d.stock, // note: we'll filter out insufficient stock quantity later
            date: d.delivery_date,
            data: d,
            entpData: entpStockMap[d.entp_stock_id] || {},
          }));

        setAvailableDates(availableDates);
      } catch (err) {
        //
      }
      setIsLoading(false);
      setInitialized(true);
    };
    _fetchCalendar();
  }, [item.id, item.logisticType, item.quantity, isStaff]);

  const initShowDate = React.useMemo(() => {
    if (!isStaff && !defaultSelectedDateStr && availableDates.length > 0) {
      let _date = availableDates[0].date;
      let _initShowDate = moment(_date, 'YYYY-MM-DD').toDate();
      return _initShowDate;
    }
    return tomorrow;
  }, [defaultSelectedDateStr, availableDates, isStaff]);

  const enableDates = React.useMemo(() => {
    // !selfPick && _minDate ~ _maxDate && isMonday / isSunday
    let _allDates = generateDates(
      moment(_minDate).format('YYYY-MM-DD'),
      moment(_maxDateCouldBuy).format('YYYY-MM-DD'),
    );

    if (item.logisticType !== LOGISTICE_TYPE_CATEGORY.selfPick) {
      _allDates = _allDates.filter((d) => {
        return (
          moment(d, 'YYYY-MM-DD').day() !== 1 &&
          moment(d, 'YYYY-MM-DD').day() !== 0
        );
      });
    }

    return _allDates;
  }, [item.logisticType, _minDate, _maxDateCouldBuy]);

  function _isQuantityValid(calendarDate, item) {
    return (
      calendarDate.stock >= (item.quantity || 0) &&
      (calendarDate.entpData?.id
        ? calendarDate.entpData.total_stock >= item.quantity
        : true)
    );
  }

  const dateChildrenComps = React.useMemo(() => {
    return availableDates.map((d) => {
      // filter out insufficient stock quantity later
      const couldBuy = isStaff || _isQuantityValid(d, item);

      return {
        date: moment(d.date, 'YYYY-MM-DD').toDate(),
        comp: couldBuy ? (
          <SellStatusComp
            text={`${item.title}${isStaff ? ` (${d.stock})` : ''}`}
            extraText={
              isStaff && d.entpData?.id
                ? `總庫存量（${d.entpData.total_stock}）`
                : ''
            }
            color="#73A05C"
          />
        ) : null,
      };
    });
  }, [availableDates]);

  const _onDateSelect = React.useCallback(
    (dt) => {
      let _onClickedDateStr = moment(dt).format('YYYY-MM-DD');
      let _foundCalendarDate = availableDates.find(
        (d) => d.date === _onClickedDateStr,
      );

      if (_foundCalendarDate && _isQuantityValid(_foundCalendarDate, item)) {
        onSelect(_onClickedDateStr, _foundCalendarDate.data);
      }
    },
    [availableDates],
  );

  React.useEffect(() => {
    if (!isStaff && initialized) {
      try {
        let couldBuyDates = dateChildrenComps.filter(
          (d) => !!d.comp && d.date > new Date(),
        );
        if (couldBuyDates.length === 0) {
          Gtag('event', 'out-of-stock', {
            product: product,
            product_id: product?.id,
            product_name: product?.name,
            spec_id: item.id,
            logistic_type: item.logisticType,
            quantity: item.quantity,
            spec_name: item.title,
          });
        }
      } catch (err) {
        //
      }
    }
  }, [availableDates, initialized, isStaff, item, dateChildrenComps, product]);

  return (
    <Wrapper>
      {isLoading && (
        <LoadingMask
          onClick={(e) => {
            e.preventDefault(); // block all click events when loading, prevent user click on calendar
          }}>
          <Spin
            indicator={<Icon.LoadingOutlined size={60} color={'#fff'} spin />}
          />
        </LoadingMask>
      )}

      <div className="hints">
        <span className="warning">
          溫馨提醒：此日期以台灣台北時間為主，請海外訂購下單客戶注意
        </span>
      </div>
      <div className="hints">
        <SellStatusComp text="可訂購" color="#73A05C" />
        <SellStatusComp text="無法訂購" color="#995A5A" />
        <SellStatusComp text="未開放" color="#a1a1a9" />
      </div>
      <Calendar
        key={`${isLoading ? 'loading' : 'done'}`}
        defaultValue={tomorrow}
        selectedDt={_selectedDate}
        defaultValue={_selectedDate}
        disablePast={false}
        year={initShowDate.getFullYear()}
        month={initShowDate.getMonth()}
        minDate={_minDate}
        maxDate={_maxDateCouldBuy}
        enabledDatesArray={enableDates}
        dateChildrenComps={dateChildrenComps}
        defaultDateChildrenComp={
          <SellStatusComp text="無法訂購" color="#995A5A" />
        }
        futureDateChildrenComp={
          <SellStatusComp text="未開放" color="#a1a1a9" />
        }
        onSelect={_onDateSelect}
      />
    </Wrapper>
  );
}

const LoadingMask = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: rgba(183, 183, 183, 0.4);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  position: relative;
  border: 1px solid pink;

  & > .hints {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 5px;

    & > .warning {
      color: var(--color-red);
    }
  }
`;

function SellStatusComp(props) {
  const {text, extraText, color} = props;
  return (
    <SellStatusCompWrapper color={color}>
      <div className="row">
        <div className="dot desktop" />
        <span className="desktop">{text}</span>
      </div>
      {extraText && (
        <div className="row" style={{marginTop: 5}}>
          <div className="dot desktop" />
          <span className="desktop">{extraText}</span>
        </div>
      )}
    </SellStatusCompWrapper>
  );
}

const SellStatusCompWrapper = styled.div`
  font-size: 14px;
  color: ${(props) => props.color};
  letter-spacing: 0.4px;
  display: flex;
  flex-direction: column;
  padding: 4px 9px;
  & .row {
    display: flex;
    align-items: center;
  }
  & .dot {
    background-color: ${(props) => props.color};
    border-radius: 50%;
    width: 8px;
    height: 8px;
    margin-right: 7px;
  }
`;

export default ReserveCalendar;
